import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd';
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage';
import BaseListing from 'src/BaseListings';
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications';
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper';
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector';
import { wtFormatRoundedValue } from 'src/helpers/commons/utils';
import WidgetPartySelector from 'src/widgets/Selectors/WidgetPartySelector';
import WidgetEmployeeSelector from 'src/widgets/Selectors/WidgetEmployeeSelector';
import { wtGetDateTimeFormat } from 'src/helpers/commons/moment';
import { SOURCE_TYPE_DROPDOWN_OPTIONS, SOURCE_TYPE_NAMES } from 'src/models/vouchers';

const JVReport = () => {
  const Base = new BaseListing('jv_report');
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [branchId, setBranchId] = useState(0);
  const [branchName, setBranchName] = useState('');
  const [JVData, setJVData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [grandTotals, setGrandTotals] = useState({ debit: 0, credit: 0 });
  const branches = wtGetFromLocalStorage("branches");
  const [parties, setParties] = useState([])
  const [employees, setEmployees] = useState([])

  const [dateRange, setDateRange] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [voucherType, setVoucherType] = useState(0);
  const [voucherTypeName, setVoucherTypeName] = useState('');
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(null)
  const [employeeName, setEmployeeName] = useState('')
  const [employeeId, setEmployeeId] = useState(null)
  const [SourceType, setSourceType] = useState(0)

  const changeValues = (key, val) => {
    setJVData([]);
    setGrandTotals({ debit: 0, credit: 0 });
    if (key === 'branch_id') {
      setBranchId(val.value);
      setBranchName(val.label);
    }
    if (key === 'date_range') {
      setDateRange(val);
    }
    if (key === 'type') {
      setVoucherType(val.value);
      setVoucherTypeName(val.label);
    }
    if (key === 'party_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'employee_id') {
      setEmployeeId(val?.value ?? null)
      setEmployeeName(val?.label ?? '')
    }
    if (key === 'source_type') {
      setSourceType(val.value)
    }
  };

  useEffect(() => {
    Base.dropdownData('voucher_types,parties,employees', {}, [setVoucherTypes, setParties, setEmployees], 1);
  }, []);

  const buildReportParams = (page = currentPage, size = pageSize) => ({
    _page_index: page,
    _page_size: size,
    branch_id: branchId,
    from_date: fromDate,
    to_date: toDate,
    type: voucherType,
    party_id: PartyId,
    employee_id: employeeId,
    source_type: SourceType,
  });

  const getReportData = (page = 1, size = pageSize) => {
    if (dateRange.length < 2) {
      setGrandTotals({ debit: 0, credit: 0 });
      lzNotifyErrorMessageFromResult("Please select date range");
      return;
    }
    setIsLoading(true);
    wtApiCall(
      'reports/jv_report',
      buildReportParams(page, size),
      'get',
      (response) => {
        setJVData(response.data.data);
        setTotalRecords(response.data.total);
        setCurrentPage(response.data.current_page);
        setPageSize(response.data.per_page);
        setGrandTotals({
          debit: response.data.grand_total_debit || 0,
          credit: response.data.grand_total_credit || 0,
        });
        setIsLoading(false);
      },
      (error) => {
        setGrandTotals({ debit: 0, credit: 0 });
        lzNotifyErrorMessageFromResult(error);
        setIsLoading(false);
      },
    );
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'voucher_date',
      key: 'voucher_date',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'JV Number',
      dataIndex: 'jv_number',
      key: 'jv_number',
      render: (text, record) => [record.type_short_name, record.company_voucher_no, record.voucher_no]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join('-')
        .replace(/\s+/g, ''),
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Monthly Voucher No',
      dataIndex: 'monthly_voucher_no',
      key: 'monthly_voucher_no',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      render: branch => branch ? branch.name : '',
      width: 150,
      fixed: 'left',
    },
    {
      title: (
        <div>
          <span style={{ marginRight: '19.5%' }}>Account Name</span>
          <span style={{ marginRight: '6%' }}>Debit</span>
          <span style={{ marginRight: '6%' }}>Credit</span>
          <span style={{ marginRight: '10%' }}>Narration</span>
        </div>
      ),
      key: 'details',
      render: (text, record) => {
        const totalDebit = record.detail.reduce((sum, item) => sum + (item.debit || 0), 0);
        const totalCredit = record.detail.reduce((sum, item) => sum + (item.credit || 0), 0);

        return (
          <>
            {record.detail.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: index !== record.detail.length - 1 ? '1px solid #f0f0f0' : 'none', // Add line except for the last row
                }}
              >
                <span style={{ flex: 1.5 }}>{item.account_name}</span>
                <span style={{ flex: 0.5 }}>
                  {item.debit <= 0 ? '-' : wtFormatRoundedValue(item.debit, '')}
                </span>
                <span style={{ flex: 0.5 }}>
                  {item.credit <= 0 ? '-' : wtFormatRoundedValue(item.credit, '')}
                </span>
                <span style={{ flex: 2.5 }}>{item.narration || '-'}</span>
              </div>
            ))}
            {/* Total Row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 0',
                fontWeight: 'bold',
                borderTop: '1px solid #f0f0f0', // Top border for total row
                marginTop: '5px',
              }}
            >
              <span style={{ flex: 1.5 }}>Total</span>
              <span style={{ flex: 0.5 }}>{wtFormatRoundedValue(totalDebit, '')}</span>
              <span style={{ flex: 0.5 }}>{wtFormatRoundedValue(totalCredit, '')}</span>
              <span style={{ flex: 2.5 }}>-</span>


            </div>
          </>
        );
      },
      width: '49%',
    },
    {
      title: 'Party Name',
      dataIndex: 'detail',
      key: 'party_name',
      width: '6%',
      ellipsis: true,
      render: (details) => {
        const lastDetail = details[details.length - 1];
        return lastDetail ? lastDetail.party_name : '-';
      },
      onCell: () => ({
        style: { whiteSpace: 'normal', wordWrap: 'break-word' },
      }),
    },
    {
      title: 'Employee Name',
      dataIndex: 'detail',
      key: 'employee_full_name',
      width: '8%',
      ellipsis: true,
      render: (details) => {
        const lastDetail = details[details.length - 1];
        return lastDetail ? lastDetail.employee_full_name : '-';
      },
      onCell: () => ({
        style: { whiteSpace: 'normal', wordWrap: 'break-word' },
      }),
    },
    {
      title: 'NTN',
      dataIndex: 'detail',
      key: 'ntn',
      width: '6%',
      ellipsis: true,
      render: (details) => {
        const lastDetail = details[details.length - 1];
        return lastDetail ? lastDetail.ntn : '-';
      },
      onCell: () => ({
        style: { whiteSpace: 'normal', wordWrap: 'break-word' },
      }),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      render: created_by => created_by ? created_by.name : '',
      width: '6%'
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '5%',
      render: (created_at) => wtGetDateTimeFormat(created_at),
    },
    {
      title: 'Reviewed By',
      dataIndex: 'approved_by',
      key: 'approved_by',
      render: approved_by => approved_by ? approved_by.name : '',
      width: '6%'
    },
    {
      title: 'Reviewed At',
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: '5%',
      render: (approved_at) => wtGetDateTimeFormat(approved_at),
    },
    {
      title: 'Approved By',
      dataIndex: 'posted_by',
      key: 'posted_by',
      render: posted_by => posted_by ? posted_by.name : '',
      width: '6%'
    },
    {
      title: 'Approved At',
      dataIndex: 'posted_at',
      key: 'posted_at',
      width: '5%',
      render: (posted_at) => wtGetDateTimeFormat(posted_at),

    },
  ];

  const print = () => {
    wtApiPostWithContentTypeHandling('reports/jv_report/print', {
      branch_id: branchId,
      from_date: fromDate,
      to_date: toDate,
      type: voucherType,
      party_id: PartyId,
      employee_id: employeeId,
      source_type: SourceType,
    });
  }

  const exportToExcel = async () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult("Please select date range");
      return;
    }
    setIsLoading(true);
    await wtApiPostWithContentTypeHandling('reports/jv_report/export', buildReportParams(1, pageSize));
    setIsLoading(false);
  }

  const onTableChange = (pagination) => {
    const nextPage = pagination.current || 1;
    const nextPageSize = pagination.pageSize || pageSize;
    setCurrentPage(nextPage);
    setPageSize(nextPageSize);
    getReportData(nextPage, nextPageSize);
  };

  return (
    <div className='container-fluid' style={{ padding: "1em", backgroundColor: "white", minHeight: "100vh", fontFamily: "Helvetica" }}>
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: "100%", margin: '0 auto' }}
      >
        <Card style={{ width: "100%" }} title={<span style={{ fontSize: '26px', marginLeft: "45%" }}>JV Report</span>}>
          <Row gutter={[12, 12]}>

            <Col xs={15} sm={10} md={4}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value);
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'));
                    setToDate(value[1].format('YYYY-MM-DD'));
                  } else {
                    setFromDate(null);
                    setToDate(null);
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Voucher Type</label>
              <WidgetSelector
                selectedValue={voucherType}
                displayValue={voucherTypeName}
                options={voucherTypes}
                showCloudIcon={false}
                onChange={(values) => changeValues('type', values)}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Party</label>
              <WidgetPartySelector
                onChange={(value) => changeValues('party_id', value)}
                parties={parties}
                displayValue={PartyName}
                value={PartyId}
                reloadDropdownData={() => reloadParties()}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Employee</label>
              <WidgetEmployeeSelector
                employees={employees}
                displayValue={employeeName}
                value={employeeId}
                onChange={(values) => changeValues('employee_id', values)}
                reloadDropdownData={() => Base.dropdownData('employees', {}, [setEmployees], 1)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Source Type</label>
              <WidgetSelector
                selectedValue={SourceType}
                displayValue={SOURCE_TYPE_NAMES[SourceType]}
                options={SOURCE_TYPE_DROPDOWN_OPTIONS}
                showCloudIcon={false}
                onChange={(values) => changeValues('source_type', values)}
                size={'sm'}
              />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Button style={{ marginTop: 30, marginRight: 5 }} type="primary" htmlType="submit" disabled={isLoading ? true : false}>
                Search
              </Button>
              <Button style={{ marginTop: 30, marginRight: 5, backgroundColor: "red", color: "#fff" }} onClick={print} disabled={isLoading ? true : false}>Print</Button>
              <Button style={{ marginTop: 30, backgroundColor: "#4BB543", color: "#fff" }} onClick={exportToExcel} disabled={isLoading ? true : false}>Export</Button>
            </Col>
          </Row>
        </Card>
      </Form>
      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={JVData}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize,
              total: totalRecords,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100, 200],
              position: ['bottomCenter'],
            }}
            onChange={onTableChange}
            bordered
            scroll={{ x: 2400, y: 600 }}
            sticky
          />
          {JVData.length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 16,
              }}
            >
              <div
                style={{
                  minWidth: 360,
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '12px 16px',
                  backgroundColor: '#f6ffed',
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Grand Total</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <span>Total Debit</span>
                  <span style={{ fontWeight: 700 }}>
                    {wtFormatRoundedValue(grandTotals.debit, '')}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 6 }}>
                  <span>Total Credit</span>
                  <span style={{ fontWeight: 700 }}>
                    {wtFormatRoundedValue(grandTotals.credit, '')}
                  </span>
                </div>
              </div>
            </div>
          )}

        </Spin>
      </Card>
    </div>
  );
};

export default JVReport;
